import { formatPrice, TextLink } from '@/modules/Frontend/UiBlocks'

import { Separator } from '../../../../Theme'
import { buildTbiLabel, computeTbiInstallment, TBI_INSTALLMENTS_URL } from '../../../lib'
import { useProductAttributes } from '../../../state'

/**
 * TBI Bank FusionPay installment card.
 *
 * Config comes from window.reactConfig.catalog.tbi (see FusionPayConfigProvider).
 * The monthly installment mirrors the authoritative server render
 * TbiBank\FusionPay\Helper\Data::getOptions() — see computeTbiInstallment().
 *
 * Also renders the two-tier TBI label (BNPL 0% below bnplMax vs standard fixed
 * credit above it), with every number sourced from tbiConfig — no hardcoded
 * amounts or periods.
 *
 * The whole card links to /cum-cumpar.html#rate-tbi (same target as the homepage
 * TrustBar TBI utility card), with a short hover hint via the title attribute.
 *
 * Note: like PriceInfo, the price is the parent's minimum-variant price and is NOT
 * reactive to configurable variant selection — kept consistent with the price shown.
 */
export function TbiCard() {
  const { tbiConfig, productAttributes } = useProductAttributes()

  if (!tbiConfig) return null

  const price = productAttributes?.price_range?.minimum_price?.final_price?.value ?? 0
  const { visible, installment } = computeTbiInstallment(price, tbiConfig)

  if (!visible) return null

  const label = buildTbiLabel(tbiConfig)

  return (
    <>
      <Separator className="my-3 xl:my-5" />
      <TextLink
        href={TBI_INSTALLMENTS_URL}
        title="Vezi cum cumperi în rate cu TBI Bank"
        className="block bg-cards-bg p-2.5 md:px-5 md:py-5 text-center md:text-left rounded-md transition hover:shadow-md hover:bg-border-color/40"
      >
        <div className="flex flex-col items-center md:flex-row md:items-center gap-4 mb-2">
          <div className="md:w-1/2 flex justify-center md:justify-start">
            <img src={tbiConfig.logoUrl} alt="TBI Bank" className="max-w-[120px]" />
          </div>
          <div className="md:w-1/2 flex items-baseline gap-2 justify-center md:justify-start">
            <span className="text-sm text-text-color">de la</span>
            <span className="text-xl md:text-2xl font-bold text-custom-red">{formatPrice(installment)}</span>
            <span className="text-lg font-semibold text-text-color">/ lună</span>
          </div>
        </div>

        <div className="mb-2 grid grid-cols-2 gap-2 text-left">
          <div className="rounded-md bg-black px-3 py-2.5 text-white">
            {label.bnplPeriod > 0 && (
              <p className="text-sm font-bold leading-tight xl:text-base">{label.bnplPeriod} rate</p>
            )}
            <p className="text-sm font-bold leading-tight xl:text-base">0% dobândă</p>
            <p className="mt-1 text-xs leading-tight">Cumpărături de până la {label.thresholdLabel}</p>
          </div>
          <div className="rounded-md bg-tbi-lime px-3 py-2.5 text-black">
            {label.hasStandardTier && (
              <p className="text-sm font-bold leading-tight xl:text-base">{label.standardPeriodLabel} rate</p>
            )}
            <p className="text-sm font-bold leading-tight xl:text-base">Dobândă fixă</p>
            <p className="mt-1 text-xs leading-tight">Cumpărături peste {label.thresholdLabel}</p>
          </div>
        </div>

        <h3 className="font-montserrat text-sm md:text-lg font-semibold">Cumpără acum, plătește mai târziu</h3>
      </TextLink>
    </>
  )
}
